"use client";

import { useLaunchActiveModel } from "@/components/virtualspace/launch-active-model-context";
import { cn } from "@/lib/utils";

/**
 * Floor-picker strip (2026-05-23, Phase 1).
 *
 * Vertical pills on the LEFT edge, centered, mimicking an
 * elevator panel. Auto-renders only when:
 *   - 2..6 siblings with explicit, unique floor labels. Repeated
 *     captures and named areas use the full-label model picker.
 *   - The page is wide enough to host a left-edge strip without
 *     overlapping Matterport's left-edge search icon (top-left
 *     corner is Matterport-owned)
 *
 * Placement:
 *   left: 16px (clears Matterport's left-edge cluster which sits
 *         at left: 8-12px depending on chrome state)
 *   top: 50%, translate(-50% Y) for vertical center
 *
 * Each pill shows the COMPACT label form (the short prefix like
 * "B1" / "G" / "M" / "1" / "R" — extracted from the full label).
 * On hover the full label appears as a tooltip pill to the right
 * of the active button.
 */
export function LaunchElevatorStrip() {
  // Self-mounts when inside a LaunchActiveModelProvider; renders
  // null outside one (server-side preview, operator pages,
  // multi-block Orbit/Landing — all paths where there's no
  // multi-model navigation to surface).
  const ctx = useLaunchActiveModel();
  if (!ctx) return null;
  const { siblings, activeDeliverableId, setActiveDeliverableId } = ctx;
  // A date or arbitrary area is not a floor. Ambiguous sets and phones
  // use the labeled model picker instead of indistinguishable buttons.
  const floorLabels = getFloorNavigationLabels(siblings.map((s) => s.label));
  if (!floorLabels) return null;

  return (
    <div
      className="hidden sm:block absolute left-[max(1rem,env(safe-area-inset-left))] top-1/2 -translate-y-1/2 z-40 pointer-events-none [@media(max-height:480px)]:hidden"
      role="group"
      aria-label="Quick floor navigation"
    >
      <div className="inline-flex flex-col gap-1 rounded-2xl bg-black/70 backdrop-blur-xl p-1 shadow-lg shadow-black/30 border border-white/15 pointer-events-auto">
        {siblings.map((s, index) => {
          const isActive = s.deliverableId === activeDeliverableId;
          const compact = floorLabels[index];
          return (
            <button
              key={s.deliverableId}
              type="button"
              onClick={() => setActiveDeliverableId(s.deliverableId)}
              aria-label={`Switch to ${s.label}`}
              aria-pressed={isActive}
              className={cn(
                "group relative inline-flex items-center justify-center min-w-11 min-h-11 px-2 rounded-xl text-xs font-semibold tracking-wider tabular-nums transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white touch-manipulation",
                isActive
                  ? "bg-white text-black shadow-sm"
                  : "text-white/85 hover:bg-white/15 hover:text-white",
              )}
            >
              <span>{compact}</span>
              {/* Tooltip with full label, appears to the right on
                  hover/focus. Pointer-events-none so it doesn't
                  intercept clicks on neighboring pills. */}
              <span
                className={cn(
                  "absolute left-full top-1/2 -translate-y-1/2 ml-2 max-w-[min(14rem,calc(100vw-6rem))] truncate px-2 py-1 rounded-md text-xs pointer-events-none transition-opacity motion-reduce:transition-none",
                  "bg-black/85 text-white border border-white/15 shadow-lg shadow-black/40",
                  "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
                )}
              >
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Distill a long sibling label into a 1-3 char compact form for
 * the elevator pill. Exported for unit testing.
 *
 * Rules:
 *   "Basement", "Basement 2"     → "B" / "B2"
 *   "Sub-cellar"                 → "SC"
 *   "Ground", "Ground Floor"     → "G"
 *   "Lobby"                      → "L"
 *   "Mezzanine"                  → "M"
 *   "Floor 1", "1F", "1"         → "1"
 *   "Floor 12"                   → "12"
 *   "Roof", "Rooftop", "PH"      → "R" / "PH"
 *   Anything else                → first 2-3 chars of the label
 */
export function compactLabel(label: string): string {
  const lc = label.trim().toLowerCase();
  if (!lc) return "?";
  if (/^basement/.test(lc)) {
    const m = lc.match(/(\d+)/);
    return m ? `B${m[1]}` : "B";
  }
  if (/^b\d/.test(lc)) {
    const m = lc.match(/(\d+)/);
    return m ? `B${m[1]}` : "B";
  }
  if (/^(sub-cellar|subcellar|sub cellar)/.test(lc)) return "SC";
  if (/^cellar/.test(lc)) return "C";
  if (/^(ground|main)/.test(lc)) return "G";
  if (/^lobby/.test(lc)) return "L";
  if (/^mezz/.test(lc)) return "M";
  if (/^(penthouse|ph)/.test(lc)) return "PH";
  if (/^(roof|rooftop)/.test(lc)) return "R";
  // Numeric: pull the first integer.
  const m = lc.match(/(\d+)/);
  if (m) return m[1]!;
  // Fallback: first 2-3 chars of the trimmed label, uppercased.
  return label.trim().slice(0, 3).toUpperCase();
}

/** Only explicit floor names may become elevator buttons. Never infer floors
 * from the first number in an arbitrary capture/date label. */
export function getFloorNavigationLabels(labels: readonly string[]): string[] | null {
  if (labels.length < 2 || labels.length > 6) return null;
  const compact: string[] = [];
  for (const label of labels) {
    // A clear floor prefix may have a descriptive suffix, e.g. Floor 2 · East.
    const floor = label.split(/\s+[·|:–—]\s*/)[0]!.trim().toLowerCase();
    const numbered = floor.match(/^(?:floor\s+|level\s+|l)?(-?\d{1,3})(?:f)?$/);
    const basement = floor.match(/^(?:basement\s*|b)(\d{1,2})?$/);
    let value: string;
    if (numbered) value = String(Number(numbered[1]));
    else if (basement) value = basement[1] ? `B${Number(basement[1])}` : "B";
    else if (/^(ground(?: floor)?|main(?: floor)?|g)$/.test(floor)) value = "G";
    else if (/^(lobby|l)$/.test(floor)) value = "L";
    else if (/^(mezzanine|mezz|m)$/.test(floor)) value = "M";
    else if (/^(sub[- ]?cellar|sc)$/.test(floor)) value = "SC";
    else if (/^(cellar|c)$/.test(floor)) value = "C";
    else if (/^(penthouse|ph)$/.test(floor)) value = "PH";
    else if (/^(roof|rooftop|r)$/.test(floor)) value = "R";
    else return null;
    if (compact.includes(value)) return null;
    compact.push(value);
  }
  return compact;
}
